import type { InteractionStyleType, VariableADTvalue } from '@lighthouse/core'
import { useMemo } from 'react'

import { type FieldInputConfig, useAppContainerContext, useAppThemeContext } from '../../components'
import type { ThemeDesignInitConfig } from '../../constants'
import { themeFieldDesignConfig } from '../../constants'
import { getBackgroundStyle, getBorderStyle, getRadiusStyle, getShadowStyle, getTextColorStyle } from '../../utils/design'

type GetFieldInteractionStylesOptions = {
    isSelectField?: boolean
    parseVariableImage?: (v: VariableADTvalue) => string
}

export type GetFieldInteractionStylesReturn = Record<'input' | 'icon', Record<InteractionStyleType, React.CSSProperties>> &
    Partial<Record<'other' | 'otherIcon' | 'extra', Record<InteractionStyleType, React.CSSProperties>>>

export type FieldStyles = Record<'input' | 'icon', React.CSSProperties & { placeholderColor?: string }> &
    Partial<Record<'other' | 'otherIcon' | 'extra', React.CSSProperties>>

export const useFieldInteractionStyles = (
    config: FieldInputConfig,
    options: GetFieldInteractionStylesOptions
): GetFieldInteractionStylesReturn | undefined => {
    const { breakPoint } = config
    const { appId } = useAppContainerContext()
    const { theme } = useAppThemeContext()
    const { isSelectField, parseVariableImage } = options
    const { inputType } = config
    const breakPointDesign = breakPoint?.design
    // eslint-disable-next-line unicorn/consistent-destructuring
    const relativeSelectMode = inputType === 'relativeSelect' ? config.relativeSelect?.showMode : undefined
    const { hoverStyle, activeStyle, disabledStyle, selectDesign, personDesign, fileDesign } = useMemo(() => {
        const styleTheme = breakPointDesign?.theme
        if (!styleTheme || styleTheme === 'CUSTOM') {
            return {
                hoverStyle: breakPoint?.hoverStyle,
                activeStyle: breakPoint?.activeStyle,
                disabledStyle: breakPoint?.disabledStyle,
                selectDesign: breakPoint?.selectDesign,
                personDesign: breakPoint?.personDesign,
                fileDesign: breakPoint?.fileDesign
            }
        }
        return themeFieldDesignConfig(inputType, { theme, relativeSelectMode })[styleTheme] as ThemeDesignInitConfig
    }, [breakPointDesign?.theme, inputType, theme, relativeSelectMode, breakPoint])

    return useMemo(() => {
        const { palettes } = theme
        switch (inputType) {
            case 'text': {
                return {
                    input: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                  ...getRadiusStyle(activeStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                  ...getRadiusStyle(disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    },
                    icon: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    },
                    extra: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes)
                              }
                            : {}
                    }
                }
            }
            case 'phoneNumber': {
                return {
                    input: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                  ...getRadiusStyle(activeStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                  ...getRadiusStyle(disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    },
                    icon: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    },
                    extra: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                  ...getRadiusStyle(activeStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                  ...getRadiusStyle(disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    }
                }
            }

            case 'relativeSelect': {
                const { relativeSelect } = config
                const showMode = relativeSelect?.showMode
                if (!isSelectField) {
                    if (showMode === 'tag') {
                        return {
                            input: {
                                hoverStyle: hoverStyle?.open
                                    ? {
                                          ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                          ...getRadiusStyle(hoverStyle?.design?.radius),
                                          ...getBackgroundStyle({
                                              appId,
                                              value: hoverStyle?.design?.background,
                                              palettes,
                                              parseVariableImage
                                          }),
                                          ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                          ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                                      }
                                    : {},
                                activeStyle: activeStyle?.open
                                    ? {
                                          ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                          ...getRadiusStyle(activeStyle?.design?.radius),
                                          ...getBackgroundStyle({
                                              appId,
                                              value: activeStyle?.design?.background,
                                              palettes,
                                              parseVariableImage
                                          }),
                                          ...getBorderStyle(activeStyle?.design?.border, palettes),
                                          ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                                      }
                                    : {},
                                disabledStyle: disabledStyle?.open
                                    ? {
                                          ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                          ...getRadiusStyle(disabledStyle?.design?.radius),
                                          ...getBackgroundStyle({
                                              appId,
                                              value: disabledStyle?.design?.background,
                                              palettes,
                                              parseVariableImage
                                          }),
                                          ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                          ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                                      }
                                    : {}
                            },
                            icon: {
                                hoverStyle: {},
                                activeStyle: {},
                                disabledStyle: {}
                            }
                        }
                    }
                    if (showMode === 'checkbox') {
                        return {
                            input: {
                                hoverStyle: hoverStyle?.open
                                    ? {
                                          ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                          ...getBackgroundStyle({
                                              appId,
                                              value: hoverStyle?.design?.background,
                                              palettes,
                                              parseVariableImage
                                          }),
                                          ...getBorderStyle(hoverStyle?.design?.border, palettes)
                                      }
                                    : {},
                                activeStyle: activeStyle?.open
                                    ? {
                                          ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                          ...getBackgroundStyle({
                                              appId,
                                              value: activeStyle?.design?.background,
                                              palettes,
                                              parseVariableImage
                                          }),
                                          ...getBorderStyle(activeStyle?.design?.border, palettes)
                                      }
                                    : {},
                                disabledStyle: disabledStyle?.open
                                    ? {
                                          ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                          ...getBackgroundStyle({
                                              appId,
                                              value: disabledStyle?.design?.background,
                                              palettes,
                                              parseVariableImage
                                          }),
                                          ...getBorderStyle(disabledStyle?.design?.border, palettes)
                                      }
                                    : {}
                            },
                            icon: {
                                hoverStyle: {},
                                activeStyle: {},
                                disabledStyle: {}
                            }
                        }
                    }

                    return {
                        input: {
                            hoverStyle: hoverStyle?.open
                                ? {
                                      ...getRadiusStyle(hoverStyle?.design?.radius),
                                      ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                      ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                      ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                                  }
                                : {},
                            activeStyle: activeStyle?.open
                                ? {
                                      ...getRadiusStyle(activeStyle?.design?.radius),
                                      ...getBackgroundStyle({
                                          appId,
                                          value: activeStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(activeStyle?.design?.border, palettes),
                                      ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                                  }
                                : {},
                            disabledStyle: disabledStyle?.open
                                ? {
                                      ...getRadiusStyle(disabledStyle?.design?.radius),
                                      ...getBackgroundStyle({
                                          appId,
                                          value: disabledStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                      ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                                  }
                                : {}
                        },
                        icon: {
                            hoverStyle: hoverStyle?.open
                                ? {
                                      ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                                  }
                                : {},
                            activeStyle: activeStyle?.open
                                ? {
                                      ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                                      // ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  }
                                : {},
                            disabledStyle: disabledStyle?.open
                                ? {
                                      ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                                  }
                                : {}
                        },
                        other: {
                            hoverStyle: selectDesign?.hoverStyle?.open
                                ? {
                                      ...getTextColorStyle(selectDesign?.hoverStyle?.design?.color, palettes),
                                      ...getRadiusStyle(selectDesign?.hoverStyle?.design?.radius),
                                      ...getBackgroundStyle({
                                          appId,
                                          value: selectDesign?.hoverStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(selectDesign?.hoverStyle?.design?.border, palettes),
                                      ...getShadowStyle(selectDesign?.hoverStyle?.design?.shadow, palettes)
                                  }
                                : {},
                            activeStyle: selectDesign?.activeStyle?.open
                                ? {
                                      ...getTextColorStyle(selectDesign?.activeStyle?.design?.color, palettes),
                                      ...getRadiusStyle(selectDesign?.activeStyle?.design?.radius),
                                      ...getBackgroundStyle({
                                          appId,
                                          value: selectDesign?.activeStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(selectDesign?.activeStyle?.design?.border, palettes),
                                      ...getShadowStyle(selectDesign?.activeStyle?.design?.shadow, palettes)
                                  }
                                : {},
                            disabledStyle: selectDesign?.disabledStyle?.open
                                ? {
                                      ...getTextColorStyle(selectDesign?.disabledStyle?.design?.color, palettes),
                                      ...getRadiusStyle(selectDesign?.disabledStyle?.design?.radius),
                                      ...getBackgroundStyle({
                                          appId,
                                          value: selectDesign?.disabledStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(selectDesign?.disabledStyle?.design?.border, palettes),
                                      ...getShadowStyle(selectDesign?.disabledStyle?.design?.shadow, palettes)
                                  }
                                : {}
                        },
                        otherIcon: {
                            hoverStyle: selectDesign?.hoverStyle?.open
                                ? {
                                      ...getTextColorStyle(selectDesign?.hoverStyle?.design?.iconColor, palettes)
                                  }
                                : {},
                            activeStyle: selectDesign?.activeStyle?.open
                                ? {
                                      ...getTextColorStyle(selectDesign?.activeStyle?.design?.iconColor, palettes)
                                      // ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  }
                                : {},
                            disabledStyle: selectDesign?.disabledStyle?.open
                                ? {
                                      ...getTextColorStyle(selectDesign?.disabledStyle?.design?.iconColor, palettes)
                                  }
                                : {}
                        }
                    }
                }

                if (showMode === 'tag') {
                    return {
                        input: {
                            hoverStyle: {},
                            activeStyle: {},
                            disabledStyle: {}
                        },
                        icon: {
                            hoverStyle: {},
                            activeStyle: {},
                            disabledStyle: {}
                        }
                    }
                }
                if (showMode === 'checkbox') {
                    return {
                        input: {
                            hoverStyle: hoverStyle?.open
                                ? {
                                      ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                      ...getBorderStyle(hoverStyle?.design?.border, palettes)
                                  }
                                : {},
                            activeStyle: activeStyle?.open
                                ? {
                                      ...getBackgroundStyle({
                                          appId,
                                          value: activeStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(activeStyle?.design?.border, palettes)
                                  }
                                : {},
                            disabledStyle: disabledStyle?.open
                                ? {
                                      ...getBackgroundStyle({
                                          appId,
                                          value: disabledStyle?.design?.background,
                                          palettes,
                                          parseVariableImage
                                      }),
                                      ...getBorderStyle(disabledStyle?.design?.border, palettes)
                                  }
                                : {}
                        },
                        icon: {
                            hoverStyle: {},
                            activeStyle: {},
                            disabledStyle: {}
                        }
                    }
                }

                return {
                    input: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getRadiusStyle(activeStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getRadiusStyle(disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    },
                    icon: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                                  // ...getBorderStyle(activeStyle?.design?.border, palettes),
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    }
                }
            }
            case 'person': {
                return {
                    input: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                  ...getRadiusStyle(activeStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                  ...getRadiusStyle(disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    },
                    icon: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                                  // ...getBorderStyle(activeStyle?.design?.border, palettes),
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    },
                    other: {
                        hoverStyle: personDesign?.hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(personDesign?.hoverStyle?.design?.color, palettes)
                              }
                            : {},
                        activeStyle: personDesign?.activeStyle?.open
                            ? {
                                  ...getTextColorStyle(personDesign?.activeStyle?.design?.color, palettes)
                              }
                            : {},
                        disabledStyle: personDesign?.disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(personDesign?.disabledStyle?.design?.color, palettes)
                              }
                            : {}
                    },
                    otherIcon: {
                        hoverStyle: personDesign?.hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(personDesign?.hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: personDesign?.activeStyle?.open
                            ? {
                                  ...getTextColorStyle(personDesign?.activeStyle?.design?.iconColor, palettes)
                                  // ...getBorderStyle(activeStyle?.design?.border, palettes),
                              }
                            : {},
                        disabledStyle: personDesign?.disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(personDesign?.disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    }
                }
            }
            case 'file': {
                return {
                    input: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: {},
                        disabledStyle: {}
                    },
                    icon: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: {},
                        disabledStyle: {}
                    },
                    other: {
                        hoverStyle: fileDesign?.hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(fileDesign?.hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(fileDesign?.hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({
                                      appId,
                                      value: fileDesign?.hoverStyle?.design?.background,
                                      palettes,
                                      parseVariableImage
                                  }),
                                  ...getBorderStyle(fileDesign?.hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(fileDesign?.hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: {},
                        disabledStyle: fileDesign?.disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(fileDesign?.disabledStyle?.design?.color, palettes),
                                  ...getRadiusStyle(fileDesign?.disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({
                                      appId,
                                      value: fileDesign?.disabledStyle?.design?.background,
                                      palettes,
                                      parseVariableImage
                                  }),
                                  ...getBorderStyle(fileDesign?.disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(fileDesign?.disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    },
                    otherIcon: {
                        hoverStyle: fileDesign?.hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(fileDesign?.hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: {},
                        disabledStyle: fileDesign?.disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(fileDesign?.disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    }
                }
            }
            case 'notes': {
                return {
                    input: {
                        hoverStyle: {},
                        activeStyle: {},
                        disabledStyle: {}
                    },
                    icon: {
                        hoverStyle: {},
                        activeStyle: {},
                        disabledStyle: {}
                    }
                }
            }

            default: {
                return {
                    input: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                                  ...getRadiusStyle(hoverStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(hoverStyle?.design?.border, palettes),
                                  ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.color, palettes),
                                  ...getRadiusStyle(activeStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(activeStyle?.design?.border, palettes),
                                  ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                                  ...getRadiusStyle(disabledStyle?.design?.radius),
                                  ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                                  ...getBorderStyle(disabledStyle?.design?.border, palettes),
                                  ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                              }
                            : {}
                    },
                    icon: {
                        hoverStyle: hoverStyle?.open
                            ? {
                                  ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                              }
                            : {},
                        activeStyle: activeStyle?.open
                            ? {
                                  ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                                  // ...getBorderStyle(activeStyle?.design?.border, palettes),
                              }
                            : {},
                        disabledStyle: disabledStyle?.open
                            ? {
                                  ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                              }
                            : {}
                    }
                }
            }
        }
    }, [
        theme,
        inputType,
        hoverStyle?.open,
        hoverStyle?.design?.color,
        hoverStyle?.design?.radius,
        hoverStyle?.design?.background,
        hoverStyle?.design?.border,
        hoverStyle?.design?.shadow,
        hoverStyle?.design?.iconColor,
        appId,
        parseVariableImage,
        activeStyle,
        disabledStyle?.open,
        disabledStyle?.design?.color,
        disabledStyle?.design?.radius,
        disabledStyle?.design?.background,
        disabledStyle?.design?.border,
        disabledStyle?.design?.shadow,
        disabledStyle?.design?.iconColor,
        config,
        isSelectField,
        selectDesign?.hoverStyle?.open,
        selectDesign?.hoverStyle?.design?.color,
        selectDesign?.hoverStyle?.design?.radius,
        selectDesign?.hoverStyle?.design?.background,
        selectDesign?.hoverStyle?.design?.border,
        selectDesign?.hoverStyle?.design?.shadow,
        selectDesign?.hoverStyle?.design?.iconColor,
        selectDesign?.activeStyle?.open,
        selectDesign?.activeStyle?.design?.color,
        selectDesign?.activeStyle?.design?.radius,
        selectDesign?.activeStyle?.design?.background,
        selectDesign?.activeStyle?.design?.border,
        selectDesign?.activeStyle?.design?.shadow,
        selectDesign?.activeStyle?.design?.iconColor,
        selectDesign?.disabledStyle?.open,
        selectDesign?.disabledStyle?.design?.color,
        selectDesign?.disabledStyle?.design?.radius,
        selectDesign?.disabledStyle?.design?.background,
        selectDesign?.disabledStyle?.design?.border,
        selectDesign?.disabledStyle?.design?.shadow,
        selectDesign?.disabledStyle?.design?.iconColor,
        personDesign?.hoverStyle?.open,
        personDesign?.hoverStyle?.design?.color,
        personDesign?.hoverStyle?.design?.iconColor,
        personDesign?.activeStyle?.open,
        personDesign?.activeStyle?.design?.color,
        personDesign?.activeStyle?.design?.iconColor,
        personDesign?.disabledStyle?.open,
        personDesign?.disabledStyle?.design?.color,
        personDesign?.disabledStyle?.design?.iconColor,
        fileDesign?.hoverStyle?.open,
        fileDesign?.hoverStyle?.design?.color,
        fileDesign?.hoverStyle?.design?.radius,
        fileDesign?.hoverStyle?.design?.background,
        fileDesign?.hoverStyle?.design?.border,
        fileDesign?.hoverStyle?.design?.shadow,
        fileDesign?.hoverStyle?.design?.iconColor,
        fileDesign?.disabledStyle?.open,
        fileDesign?.disabledStyle?.design?.color,
        fileDesign?.disabledStyle?.design?.radius,
        fileDesign?.disabledStyle?.design?.background,
        fileDesign?.disabledStyle?.design?.border,
        fileDesign?.disabledStyle?.design?.shadow,
        fileDesign?.disabledStyle?.design?.iconColor
    ])
}

export const useFieldStyles = (config: FieldInputConfig, options: GetFieldInteractionStylesOptions): FieldStyles | undefined => {
    const { breakPoint, inputType } = config

    const { appId } = useAppContainerContext()
    const { theme } = useAppThemeContext()
    const breakPointDesign = breakPoint?.design
    const selectDesign = breakPoint?.selectDesign
    const fileDesign = breakPoint?.fileDesign
    const personDesign = breakPoint?.personDesign
    // eslint-disable-next-line unicorn/consistent-destructuring
    const relativeSelectMode = inputType === 'relativeSelect' ? config.relativeSelect?.showMode : undefined
    const { palettes } = theme
    const design = useMemo(() => {
        const styleTheme = breakPointDesign?.theme
        if (!styleTheme || styleTheme === 'CUSTOM') {
            return breakPointDesign
        }
        return themeFieldDesignConfig(inputType, { theme, relativeSelectMode })[styleTheme]?.design
    }, [breakPointDesign, inputType, relativeSelectMode, theme])

    return useMemo(() => {
        if (!design && !personDesign && !selectDesign && !fileDesign) {
            return undefined
        }
        const { inputType } = config
        const { isSelectField, parseVariableImage } = options

        switch (inputType) {
            case 'text': {
                return {
                    input: {
                        ...getTextColorStyle(design?.color, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    icon: {
                        ...getTextColorStyle(design?.iconColor, palettes)
                    },
                    extra: {
                        ...getTextColorStyle(design?.iconColor, palettes),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes)
                    }
                }
            }
            case 'phoneNumber': {
                return {
                    input: {
                        ...getTextColorStyle(design?.color, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    icon: {
                        ...getTextColorStyle(design?.iconColor, palettes)
                    },
                    extra: {
                        ...getTextColorStyle(design?.color, palettes),
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    }
                }
            }
            case 'relativeSelect': {
                const { relativeSelect } = config
                const showMode = relativeSelect?.showMode
                if (!isSelectField) {
                    if (showMode === 'tag') {
                        return {
                            input: {
                                ...getTextColorStyle(design?.color, palettes),
                                ...getRadiusStyle(design?.radius),
                                ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                                ...getBorderStyle(design?.border, palettes),
                                ...getShadowStyle(design?.shadow, palettes)
                            },
                            icon: {}
                        }
                    }
                    if (showMode === 'checkbox') {
                        return {
                            input: {
                                ...getRadiusStyle(design?.radius),
                                ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                                ...getBorderStyle(design?.border, palettes)
                            },
                            icon: {}
                        }
                    }

                    return {
                        input: {
                            placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                            ...getRadiusStyle(design?.radius),
                            ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                            ...getBorderStyle(design?.border, palettes),
                            ...getShadowStyle(design?.shadow, palettes)
                        },
                        icon: {
                            ...getTextColorStyle(design?.iconColor, palettes),
                            placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color
                        },
                        other: {
                            ...getTextColorStyle(selectDesign?.design?.color, palettes),
                            ...getRadiusStyle(selectDesign?.design?.radius),
                            ...getBackgroundStyle({ appId, value: selectDesign?.design?.background, palettes, parseVariableImage }),
                            ...getBorderStyle(selectDesign?.design?.border, palettes),
                            ...getShadowStyle(selectDesign?.design?.shadow, palettes)
                        },
                        otherIcon: {
                            ...getTextColorStyle(selectDesign?.design?.iconColor, palettes)
                        }
                    }
                }
                if (showMode === 'tag') {
                    return {
                        input: {},
                        icon: {}
                    }
                }
                if (showMode === 'checkbox') {
                    return {
                        input: {
                            ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                            ...getBorderStyle(design?.border, palettes)
                        },
                        icon: {}
                    }
                }

                return {
                    input: {
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    icon: {
                        ...getTextColorStyle(design?.iconColor, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        // ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    }
                }
            }
            case 'file': {
                return {
                    input: {
                        ...getTextColorStyle(design?.color, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    icon: {
                        ...getTextColorStyle(design?.iconColor, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius)
                    },
                    other: {
                        ...getTextColorStyle(fileDesign?.design?.color, palettes),
                        ...getRadiusStyle(fileDesign?.design?.radius),
                        ...getBackgroundStyle({ appId, value: fileDesign?.design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(fileDesign?.design?.border, palettes),
                        ...getShadowStyle(fileDesign?.design?.shadow, palettes)
                    },
                    otherIcon: {
                        ...getTextColorStyle(fileDesign?.design?.iconColor, palettes)
                    }
                }
            }

            case 'person': {
                return {
                    input: {
                        ...getTextColorStyle(design?.color, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    icon: {
                        ...getTextColorStyle(design?.iconColor, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    other: {
                        ...getTextColorStyle(personDesign?.design?.color, palettes)
                    },
                    otherIcon: {
                        ...getTextColorStyle(personDesign?.design?.iconColor, palettes)
                    }
                }
            }

            case 'notes': {
                return {
                    input: {
                        ...getBorderStyle(breakPointDesign?.border, palettes)
                    },
                    icon: {}
                }
            }

            default: {
                return {
                    input: {
                        ...getTextColorStyle(design?.color, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                        ...getBorderStyle(design?.border, palettes),
                        ...getShadowStyle(design?.shadow, palettes)
                    },
                    icon: {
                        ...getTextColorStyle(design?.iconColor, palettes),
                        placeholderColor: getTextColorStyle(design?.placeholderColor, palettes).color,
                        ...getRadiusStyle(design?.radius),
                        ...getShadowStyle(design?.shadow, palettes)
                    }
                }
            }
        }
    }, [appId, breakPointDesign?.border, config, design, fileDesign, options, palettes, personDesign, selectDesign])
}
